import { showNotification } from "components/v2/notification/notification";
import { AES_PASS, NEW_HOST_URL } from "config/envConfig";
import CryptoJS from "crypto-js";
import amplitude from "externals/amplitude";
import { compact, flatten } from "lodash";
import magnifiAPIInstance from "services/api/magnifi/apiInstance";
import { clearChunks, loadChunks, saveChunk } from "utils/indexed-db";
import LocalStorage from "utils/LocalStorage";

import { mediaLiveInput, mediaLiveTemplateOption } from "./config/index";
import { ENTITY_TYPE, getTimeInSeconds, ORGANIZATION_ROLES, ROLE } from "./constants/phase";

export const popupCenter = ({ url, title, w, h }) => {
  // Fixes dual-screen position                             Most browsers      Firefox
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : window.screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : window.screen.height;

  const systemZoom = width / window.screen.availWidth;
  const left = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top = (height - h) / 2 / systemZoom + dualScreenTop;
  const newWindow = window.open(
    url,
    title,
    `
    scrollbars=yes,
    width=${w / systemZoom},
    height=${h / systemZoom},
    top=${top},
    left=${left}
    `,
  );
  // if (window.focus) newWindow.focus();
};

export function findOutputTemplateValue(label) {
  const option = mediaLiveTemplateOption.find((option) => option.label === label);
  return option ? option.value : null;
}

export function findMediaLiveInputValue(label) {
  const option = mediaLiveInput.find((option) => option.label === label);
  return option ? option.value : null;
}

export const fetchTagsFromClip = (clipDataObj = {}, tagListArr = []) => {
  let clipData = clipDataObj || {};
  let tagList = tagListArr || [];
  return tagList.reduce((tags, tagObj) => {
    if (tagObj?.showInTagList) {
      const tag = (clipData?.[tagObj?.fieldName] || "").toString().trim();
      const tag2 = (clipData?.[tagObj?.displayName] || "").toString().trim();
      if (tag || tag2) {
        if (tagObj?.isMultiple) {
          const tagArray = tag?.split(",") || [];
          const tag2Array = tag2?.split(",") || [];
          tags.push([...tagArray, ...tag2Array]);
        } else {
          tags.push(tag);
          tags.push(tag2);
        }
      }
    }
    tags.push(clipData.gameTime, clipData.heartbeat, clipData.shotType);
    return compact(flatten(tags));
  }, []);
};

export const getClipEventTags = (clip, clipTaggingFields) => {
  let eventsArray = clip?.filter?.split(",") || [];
  clip?.clipData?.events?.map((obj) => {
    eventsArray.push(obj.type);
    eventsArray.push(obj.playerName);
  });
  eventsArray.push(...(clip?.clipData?.taggedEvents || []));
  eventsArray.push(...(clip?.clipData?.taggedPlayers || []));

  eventsArray.push(clip?.clipData?.batsman);
  eventsArray.push(clip?.clipData?.bowler);
  eventsArray.push(clip?.clipData?.fielder);
  eventsArray.push(clip?.clipData?.player);
  const outcome = clip?.clipData?.outcome;
  eventsArray.unshift(outcome);
  eventsArray.push(...(typeof outcome === "string" ? outcome.split(",") : []));
  eventsArray = eventsArray.filter((n) => n);
  eventsArray = [...eventsArray, ...fetchTagsFromClip(clip?.clipData, clipTaggingFields)];
  eventsArray = eventsArray.map((event) => (typeof event === "string" ? event?.trim() : event));
  eventsArray = [...new Set(eventsArray)];
  eventsArray = compact(eventsArray);
  return eventsArray;
};

export const encryptString = (stringToEncrypt) => {
  const encrypted = CryptoJS.AES.encrypt(stringToEncrypt, AES_PASS).toString();
  return encrypted;
};

export const refreshToken = async () => {
  try {
    const data = {
      userId: LocalStorage.getItem("__uid"),
    };
    const response = await magnifiAPIInstance.post(`${NEW_HOST_URL}/refresh`, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: LocalStorage.getItem("authToken"),
      },
      withCredentials: true,
    });
    return response.data.data.authToken;
  } catch (e) {
    amplitude.logEvent("refresh_token_failed");
    console.log("Error", e);
  }
};

export const getLiveVideoUrl = (stream, singleClip) => {
  const value = { stream };
  if (value.stream) {
    let url = null;
    if (value.stream.vod) {
      url = value.stream.url;
    } else {
      if (value.stream.previousRecordingURLs && singleClip?.cutVersion) {
        if (value.stream.previousRecordingURLs.length < singleClip?.cutVersion) {
          url = value.stream.hlsS3URL;
        } else {
          url = value.stream.previousRecordingURLs[singleClip?.cutVersion - 1]?.hlsS3URL;
        }
      } else {
        url = value.stream.hlsS3URL;
      }
    }
    if (!url) {
      url = value.stream.url;
    }
    return url;
  }
};

export const overwritePayload = ({ streamTime: clip, startTime, endTime, title = "", isOverWrite = true }) => {
  const { liveVideoUrl, singleClip, playBackSpeed } = clip;
  const { stream: streamDetails } = singleClip;
  let currentPlayingURLData = null;
  if (streamDetails && !streamDetails.vod) {
    if (
      streamDetails.previousRecordingURLs &&
      singleClip?.cutVersion &&
      streamDetails.previousRecordingURLs.length >= singleClip?.cutVersion
    ) {
      currentPlayingURLData = streamDetails.previousRecordingURLs[singleClip?.cutVersion - 1];
    }
  }
  let payload = {
    start_time: getTimeInSeconds(startTime),
    end_time: getTimeInSeconds(endTime),
    server_address: streamDetails?.server_address,
    streamId: singleClip?.streamId,
    title: isOverWrite ? singleClip?.title : title,
    video_url: streamDetails?.vod ? streamDetails?.url : currentPlayingURLData?.hlsS3URL || liveVideoUrl,
    zee: false,
    env: "production",
    isMediaLive: "false",
    seiEnable: streamDetails?.seiEnable,
    playback_speed: playBackSpeed ? (playBackSpeed === "all" ? 1 : Number(playBackSpeed)) : 1,
    isOverWrite: isOverWrite,
    clipId: singleClip?.id,
    clipData: singleClip?.clipData || {},
    jsonTemplate: singleClip?.jsonTemplate || {},
    filter: singleClip?.filter || "",
    editedVideos: singleClip?.editedVideos || {},
    editedClipData: singleClip?.editedClipData || {},
    vod: streamDetails?.vod,
    cutVersion: singleClip.cutVersion || 1,
    cutFromPreviousURL: currentPlayingURLData ? true : false,
    hls_s3_url: liveVideoUrl?.replace("index.m3u8", "index_360p.m3u8"),
    _id: singleClip._id,
  };

  if (streamDetails?.glacierRecoveredStream || streamDetails?.status === 1 || currentPlayingURLData) {
    payload.sourceUrl = true;
    payload.videoUrl = streamDetails?.vod
      ? streamDetails?.url
      : currentPlayingURLData?.s3RecordedUrl || streamDetails?.s3RecordedUrl || "";
  }
  return payload;
};

export const getUserOrganization = (user) => {
  const isUserOrReviewer = [ROLE.USER, ROLE.REVIEWER].includes(user?.role); //check if role is user or reviewer
  const subOrgs = user?.assignedOrganization
    ?.filter((org) => org.type === ORGANIZATION_ROLES.SUB_ORGANIZATION)
    .map((org) => org.organizationId); //if sub org is assigned
  const orgs = user?.assignedOrganization
    ?.filter((org) => org.type === ORGANIZATION_ROLES.ORGANIZATION)
    ?.map((org) => org.organizationId); //if org is assigned

  const _id = isUserOrReviewer ? subOrgs || orgs : orgs;
  return _id;
};

export const getUserOrganizationType = (user) => {
  const isUserOrReviewer = [ROLE.USER, ROLE.REVIEWER].includes(user?.role); //check if role is user or reviewer
  const subOrgs = user?.assignedOrganization
    ?.filter((org) => org.type === ORGANIZATION_ROLES.SUB_ORGANIZATION)
    ?.map((org) => org.organizationId); //if sub org is assigned
  const orgs = user?.assignedOrganization
    ?.filter((org) => org.type === ORGANIZATION_ROLES.ORGANIZATION)
    ?.map((org) => org.organizationId); //if org is assigned

  const type = subOrgs?.length > 0 ? ORGANIZATION_ROLES.SUB_ORGANIZATION : ORGANIZATION_ROLES.ORGANIZATION;
  return type;
};

export const getEntityType = (entityId) => {
  // get type org || workspace
  if (entityId?.includes("org_")) {
    return ENTITY_TYPE.ORGANIZATION;
  } else if (entityId?.includes("ws_")) {
    return ENTITY_TYPE.WORKSPACES;
  } else {
    return null;
  }
};

export const createDownloadLink = async (url, openInNew = false, filename = "", onProgress, abortController) => {
  try {
    // so as to remove any query string if present in url, to get a proper file extension
    const pathname = new URL(url).pathname;

    if (!filename) {
      filename = pathname.split("/").pop();
    }
    const startByte = parseInt(localStorage.getItem(`downloadProgress_${filename}`), 10) || 0;
    await loadChunks(filename);
    let receivedLength = startByte;

    const response = await fetch(url, {
      headers: { Range: `bytes=${startByte}-` },
      ...(abortController && { signal: abortController.signal }),
    });

    if (!response.ok) {
      showNotification({
        message: `Failed to download file`,
        type: "error",
      });

      return;
    }

    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");
    const totalSize = parseInt(contentLength, 10);

    const reader = response.body.getReader();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      await saveChunk(filename, new Uint8Array(value), receivedLength);
      receivedLength += value.length;

      const progress = parseInt((receivedLength / totalSize) * 100);
      localStorage.setItem(`downloadProgress_${filename}`, receivedLength);
      if (progress <= 99 && onProgress) {
        onProgress(progress);
      }
    }

    const allChunks = await loadChunks(filename);
    const blob = new Blob(allChunks, { type: contentType });
    const downloadUrl = window.URL.createObjectURL(blob);
    if (onProgress) {
      onProgress(100);
    }

    try {
      await new Promise((resolve) => {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename ? `${filename}.${pathname.split(".").pop()}` : pathname.split("/").pop();
        if (openInNew) {
          link.target = "_blank";
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        resolve();
      });
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      window.URL.revokeObjectURL(downloadUrl);
    }

    await clearChunks(filename);
    localStorage.removeItem(`downloadProgress_${filename}`);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const createPresignedDownloadLink = async (url, openInNew = false, filename = "") => {
  // For images, fetch and create blob to force download
  if (url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || url.split("/").pop();
      if (openInNew) link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  } else {
    window.open(url, "_blank");
  }
};

export const loadScript = (src, attributes) => {
  try {
    const script = document.createElement("script");
    script.src = src;
    script.onerror = () => {
      console.error(`Script load error for ${src}`);
    };

    Object.keys(attributes).forEach((key) => {
      script.setAttribute(key, attributes[key]);
    });

    document.head.appendChild(script);
  } catch (error) {
    console.error(`Script load error for ${src}`);
  }
};

export const isEmptyArray = (arr) => !arr || arr.length === 0;

export const isErrorResponse = (response) => {
  return !response?.payload || response?.status === "error";
};

export const checkIsMac = () => {
  if (navigator.userAgentData) {
    return navigator.userAgentData.platform.toLowerCase().includes("mac");
  }
  return navigator.userAgent.toLowerCase().includes("mac");
};

export const isClipHasMetadata = (clip = {}) => {
  const hasClipDataTags = Object.values(clip.clipData || {}).some(Boolean);

  const filterTags = clip?.filter?.split(",").filter(Boolean) || [];
  const eventTags = clip?.clipData?.events?.flatMap((event) => [event.type, event.playerName].filter(Boolean)) || [];

  const combinedTags = [...filterTags, ...eventTags];

  return hasClipDataTags || combinedTags.length > 0;
};

export const generateColorHash = () => {
  const randomColor = Math.floor(Math.random() * 0xffffff);
  return `#${randomColor.toString(16).padStart(6, "0")}`;
};

export const addDownloadDispositionToUrl = (url, filename) => {
  if (!url || !filename) return url;

  try {
    if (!url.includes("cloudfront")) {
      return url;
    }

    const urlObj = new URL(url);
    const sanitizedFilename = filename.replace(/[^a-zA-Z0-9-_\s.]/g, "").trim() || "file";

    const pathname = urlObj.pathname;
    const urlExtensionMatch = pathname.match(/\.([a-zA-Z0-9]+)(\?|$)/);
    const urlExtension = urlExtensionMatch ? urlExtensionMatch[1].toLowerCase() : null;

    const validExtensions = ["mp4", "mov", "avi", "webm", "jpg", "jpeg", "png", "gif", "webp", "bmp"];
    const detectedExtension = validExtensions.includes(urlExtension) ? urlExtension : "mp4";

    const filenameLower = sanitizedFilename.toLowerCase();
    const filenameWithExtension = filenameLower.endsWith(`.${detectedExtension}`)
      ? sanitizedFilename
      : `${sanitizedFilename}.${detectedExtension}`;

    urlObj.searchParams.set("response-content-disposition", `attachment; filename="${filenameWithExtension}"`);

    return urlObj.toString();
  } catch (error) {
    console.error("Error adding download disposition to URL:", error);
    return url;
  }
};
